import { useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Select, { type SingleValue } from 'react-select';
import OptionLayoutPlace from '@/app/common/components/OptionsLayout/OptionLayoutPlace';
import SingleValueFlag from '@/app/common/components/OptionsLayout/SingleValueFlag';
import OptionLayoutIcon from '@/app/common/components/OptionsLayout/OptionLayoutIcon';
import SingleValueIcon from '@/app/common/components/OptionsLayout/SingleValueIcon';
import { breakpointsMediaQuery, useMediaQuery } from '@/helpers/utils';
import classNames from 'classnames';
import Flex from '@/components/common/Flex';
import TextLoading from '@/app/common/components/skeleton/TextLoading';

type PlaceOption = {
  value: string;
  label: string;
  flag?: string;
  icon?: string | [string, string];
};
type DeviceOption = { value: string; label: string; icon: string };
type InternetOption = { value: string; label: string };

const NewAnalysisFormSkeleton = () => {
  const isLgUp = useMediaQuery(breakpointsMediaQuery.lg);

  const [selectedPlace, setSelectedPlace] = useState<PlaceOption>({
    value: 'ir',
    label: 'سرور ایران',
    flag: `https://static.mizfa.tools/flags/ir.webp`
  });
  const [selectedDevice, setSelectedDevice] = useState<DeviceOption>({
    value: 'mobile',
    label: 'گوشی',
    icon: 'mobile-alt'
  });
  const [selectedInternet, setSelectedInternet] = useState<InternetOption>({
    value: 'wifi',
    label: 'WIFI'
  });
  return (
    <div className={`w-100 w-lg-75 mt-3 mb-2 position-relative disabled-link`}>
      <Form.Group>
        <div
          className={classNames('position-relative input-group-md', {
            'input-group': isLgUp,
            'd-flex flex-column w-100 gap-2': !isLgUp
          })}
        >
          <div style={{ width: isLgUp ? '160px' : '100%' }}>
            <Select<PlaceOption>
              menuShouldScrollIntoView={true}
              closeMenuOnSelect={true}
              isSearchable={true}
              onChange={(e: SingleValue<PlaceOption>) => {
                if (e) setSelectedPlace(e);
              }}
              options={[
                {
                  value: 'ir',
                  label: 'سرور ایران',
                  flag: `https://static.mizfa.tools/flags/ir.webp`
                },
                {
                  value: 'not_ir',
                  label: 'سرور خارج',
                  icon: 'globe'
                },
                {
                  value: 'google',
                  label: 'سرور گوگل',
                  icon: ['fab', 'google']
                }
              ]}
              classNamePrefix="react-select"
              value={selectedPlace}
              maxMenuHeight={300}
              styles={{
                control: baseStyles => ({
                  ...baseStyles,
                  paddingLeft: '0px',
                  paddingRight: '10px',
                  color: 'var(--falcon-emphasise) !important',
                  boxShadow: 'none !important',
                  cursor: 'pointer',
                  borderRadius: isLgUp ? '0px 5px 5px 0px' : '5px'
                }),
                input: baseStyles => ({
                  ...baseStyles,
                  color: 'var(--falcon-emphasise) !important',
                  boxShadow: 'none !important'
                })
              }}
              components={{
                Option: OptionLayoutPlace as never,
                SingleValue: SingleValueFlag as never
              }}
            />
          </div>
          <div style={{ width: isLgUp ? '120px' : '100%' }}>
            <Select<DeviceOption>
              menuShouldScrollIntoView={true}
              closeMenuOnSelect={true}
              isSearchable={true}
              onChange={(e: SingleValue<DeviceOption>) => {
                if (e) setSelectedDevice(e);
              }}
              options={[
                {
                  value: 'mobile',
                  label: 'گوشی',
                  icon: 'mobile-alt'
                },
                {
                  value: 'desktop',
                  label: 'رایانه',
                  icon: 'desktop'
                }
              ]}
              classNamePrefix="react-select"
              value={selectedDevice}
              maxMenuHeight={300}
              styles={{
                control: baseStyles => ({
                  ...baseStyles,
                  paddingLeft: '0px',
                  paddingRight: '10px',
                  color: 'var(--falcon-emphasise) !important',
                  boxShadow: 'none !important',
                  cursor: 'pointer',
                  borderRadius: isLgUp ? '0px' : '5px'
                }),
                input: baseStyles => ({
                  ...baseStyles,
                  color: 'var(--falcon-emphasise) !important',
                  boxShadow: 'none !important'
                })
              }}
              components={{
                Option: OptionLayoutIcon as never,
                SingleValue: SingleValueIcon as never
              }}
            />
          </div>
          <div style={{ width: isLgUp ? '120px' : '100%' }}>
            <Select<InternetOption>
              menuShouldScrollIntoView={true}
              closeMenuOnSelect={true}
              isSearchable={true}
              onChange={(e: SingleValue<InternetOption>) => {
                if (e) setSelectedInternet(e);
              }}
              options={[
                'wifi',
                'fast 4G',
                'regular 4G',
                'regular 3G',
                'good 3G',
                'slow 3G',
                'regular 2G',
                'good 2G'
              ].map(net => ({
                label: net.toUpperCase(),
                value: net
              }))}
              classNamePrefix="react-select"
              value={selectedInternet}
              maxMenuHeight={300}
              styles={{
                control: baseStyles => ({
                  ...baseStyles,
                  paddingLeft: '0px',
                  paddingRight: '10px',
                  color: 'var(--falcon-emphasise) !important',
                  boxShadow: 'none !important',
                  cursor: 'pointer',
                  borderRadius: isLgUp ? '0px' : '5px'
                }),
                input: baseStyles => ({
                  ...baseStyles,
                  color: 'var(--falcon-emphasise) !important',
                  boxShadow: 'none !important'
                })
              }}
              components={{
                Option: OptionLayoutIcon as never,
                SingleValue: SingleValueIcon as never
              }}
            />
          </div>

          <Form.Control
            type="text"
            disabled
          />
          <Flex className="flex-row-reverse flex-lg-row">
            <Button
              style={{
                borderRadius: '5px 0px 0px 5px',
                height: '100%'
              }}
              className={
                isLgUp
                  ? 'd-flex align-items-center h-100 rounded-start-0'
                  : 'rounded-3'
              }
              variant="outline-secondary"
              title="تنظیمات واکاوی"
              disabled
            >
              <FontAwesomeIcon icon="cog" />
            </Button>

            <Button
              style={{ borderRadius: '5px' }}
              variant="falcon-primary"
              className="mx-0 me-2 mx-lg-2 flex-1 justify-content-center d-flex align-items-center gap-2"
              type="submit"
              disabled
            >
              <Spinner
                style={{
                  width: '16px',
                  height: '16px'
                }}
              />
              <TextLoading width={62} height={10} isWidthPx />
            </Button>
          </Flex>
        </div>
        <Form.Control.Feedback className="d-block">
          &nbsp;
        </Form.Control.Feedback>
      </Form.Group>
    </div>
  );
};

export default NewAnalysisFormSkeleton;
